import { useLanguage } from '../LanguageContext';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import axiosInstance from '../axiosInstance';

export default function BanModal({ open, onClose, playerData, onSaved, mode = 'create' }) {
  const { t } = useLanguage();
  const editing = mode === 'edit';
  const [editType, setEditType] = useState('reason');
  const [duration, setDuration] = useState(24);
  const [unit, setUnit] = useState('hours');
  const [reason, setReason] = useState('');
  const [permanent, setPermanent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState(null);
  const inFlight = useRef(false);
  useEffect(() => {
    if (!open) return;
    setReason(editing ? playerData?.ban_reason || '' : '');
    setDuration(editing ? '' : 24); setUnit('hours'); setPermanent(false);
    setEditType('reason'); setFailure(null);
  }, [open, editing, playerData?.player_identity, playerData?.ban_start, playerData?.ban_reason]);
  const changesDuration = !editing || editType === 'duration';
  const hours = permanent ? 0 : Number(duration) * (unit === 'days' ? 24 : 1);
  const durationValid = permanent || (String(duration).trim() !== '' && Number.isInteger(Number(duration)) && hours >= 1 && hours <= 87600);
  const reasonValid = Boolean(reason.trim()) && reason.trim().length <= 200;
  const close = () => { if (!inFlight.current) onClose(); };

  const save = async event => {
    event.preventDefault();
    if (inFlight.current || !reasonValid || (changesDuration && !durationValid)) return;
    inFlight.current = true; setSaving(true); setFailure(null);
    const payload = { identity: playerData.player_identity, reason: reason.trim() };
    if (changesDuration) payload.duration = hours;
    try {
      let response;
      if (editing) {
        payload.ban_start = playerData.ban_start;
        payload.expected_reason = playerData.ban_reason;
        response = await axiosInstance.patch('/api/edit_ban', payload);
      } else {
        payload.player_name = playerData.player_name || t("Unknown");
        response = await axiosInstance.post('/api/ban_player', payload);
      }
      if (!response.data.success) { setFailure(response.data); return; }
      onSaved?.(response.data);
      onClose();
    } catch (error) {
      setFailure(error.response?.data || { error: t("The request could not be completed. Check the ban list before retrying.") });
    } finally { inFlight.current = false; setSaving(false); }
  };
  return <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
    <Box component="form" onSubmit={save}>
      <DialogTitle>{editing ? t("Edit ban") : t("Ban player")}: {playerData?.player_name || t("Unknown")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere', mb: 2 }} color="text.secondary">{playerData?.player_identity}</Typography>
        {editing && <Tabs value={editType} onChange={(_, value) => setEditType(value)} variant="fullWidth" sx={{ mb: 2 }}>
          <Tab value="reason" label={t("Reason only")} disabled={saving} />
          <Tab value="duration" label={t("Replace duration")} disabled={saving || !playerData?.is_active} />
        </Tabs>}
        <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
          {editing ? changesDuration ? t("The current ban will be removed, then replaced with this new period starting now. The previous entry stays in the history.") : t("Only the reason in the database changes. The server ban and its expiry stay the same.") : t("Choose a duration and explain why this player is being banned.")}
        </Typography>
        {editing && !playerData?.is_active && <Alert severity="info" sx={{ mb: 2 }}>{t("This ban is inactive. You can correct its reason.")}</Alert>}
        {changesDuration && <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <TextField label={editing ? t("New duration") : t("Ban duration")} type="number" value={permanent ? '' : duration} disabled={saving || permanent}
              onChange={event => setDuration(event.target.value)} inputProps={{ min: 1, max: unit === 'days' ? 3650 : 87600, step: 1 }}
              required={!permanent} sx={{ flex: '1 1 150px' }} />
            <ToggleButtonGroup value={unit} exclusive disabled={saving || permanent} onChange={(_, value) => { if (value) setUnit(value); }} aria-label={t("Duration unit")}>
              <ToggleButton value="hours">{t("Hours")}</ToggleButton><ToggleButton value="days">{t("Days")}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <FormControlLabel label={t("Permanent ban")} control={<Switch checked={permanent} disabled={saving} onChange={event => setPermanent(event.target.checked)} />} />
        </>}
        <TextField label={t("Ban reason")} value={reason} onChange={event => setReason(event.target.value)} fullWidth required multiline minRows={3}
          margin="normal" disabled={saving} inputProps={{ maxLength: 200 }} helperText={t("{count}/200 · A reason is required.", { count: reason.trim().length })} />
        {failure && <Alert severity="error" sx={{ mt: 2 }}>{t(failure.error) || t("The operation failed. The ban was not confirmed.")}
          {(failure.server_results || failure.server_responses || failure.recovery) && <details><summary>{t("Server results")}</summary><pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 12 }}>{JSON.stringify({ servers: failure.server_results || failure.server_responses, recovery: failure.recovery }, null, 2)}</pre></details>}
        </Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={saving}>{t("Cancel")}</Button>
        <Button type="submit" variant="contained" disabled={saving || !reasonValid || (changesDuration && !durationValid)}>
          {saving ? t("Saving…") : editing ? changesDuration ? t("Unban & replace period") : t("Save reason") : t("Confirm ban")}
        </Button>
      </DialogActions>
    </Box>
  </Dialog>;
}
